import { useEffect, useState } from "react";
import {
  listFreeGrants,
  createFreeGrant,
  updateFreeGrant,
  deleteFreeGrant,
} from "../lib/admin.js";
import AdminSidebar from "./AdminSidebar.jsx";

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yy}-${mm}-${dd} ${hh}:${mi}`;
}

export default function AdminFreeGrants({ onSignOut }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 등록 폼 상태
  const [email, setEmail] = useState("");
  const [childName, setChildName] = useState("");
  const [parentName, setParentName] = useState("");
  const [credits, setCredits] = useState(4);
  const [expiresMonths, setExpiresMonths] = useState(2);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // {tone:'ok'|'err', msg}

  // 인라인 편집 상태
  const [editingId, setEditingId] = useState(null);
  const [editChild, setEditChild] = useState("");
  const [editParent, setEditParent] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listFreeGrants();
      setRows(r);
    } catch (err) {
      setError(err.message ?? "불러오기 실패");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    setFeedback(null);
    if (!email.trim()) {
      setFeedback({ tone: "err", msg: "이메일을 입력해주세요" });
      return;
    }
    setSubmitting(true);
    try {
      const result = await createFreeGrant({
        email: email.trim(),
        credits: Number(credits) || 4,
        expiresMonths: Number(expiresMonths) || 2,
        note: note.trim() || null,
        childName: childName.trim() || null,
        parentName: parentName.trim() || null,
      });
      setFeedback({
        tone: "ok",
        msg: result.applied
          ? `등록 완료 — 이미 가입된 사용자라 즉시 ${credits}회 지급됐어요.`
          : `등록 완료 — 해당 이메일로 가입하면 자동 지급돼요.`,
      });
      setEmail("");
      setChildName("");
      setParentName("");
      setNote("");
      await load();
    } catch (err) {
      setFeedback({ tone: "err", msg: err.message ?? "등록 실패" });
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (row) => {
    if (row.granted_at) return;
    if (!window.confirm(`${row.email} 무료권 등록을 삭제할까요?`)) return;
    try {
      await deleteFreeGrant(row.id);
      await load();
    } catch (err) {
      alert(err.message ?? "삭제 실패");
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditChild(row.child_name ?? "");
    setEditParent(row.parent_name ?? "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditChild("");
    setEditParent("");
  };

  const saveEdit = async (row) => {
    try {
      await updateFreeGrant({
        id: row.id,
        childName: editChild.trim() || null,
        parentName: editParent.trim() || null,
      });
      setEditingId(null);
      await load();
    } catch (err) {
      alert(err.message ?? "수정 실패");
    }
  };

  const pendingCount = rows.filter((r) => !r.granted_at).length;
  const grantedCount = rows.filter((r) => r.granted_at).length;

  return (
    <div className="admin">
      <AdminSidebar active="free-grants" onSignOut={onSignOut}/>

      <main className="admin-main">
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:18}}>
          <div>
            <h1 className="admin-h1">무료권 지급</h1>
            <p className="admin-sub">
              {loading
                ? "불러오는 중..."
                : `대기 ${pendingCount}건 · 지급 완료 ${grantedCount}건`}
            </p>
          </div>
          <button className="btn btn-outline" style={{padding:'8px 16px',fontSize:12}} onClick={load}>
            ⟳ 새로고침
          </button>
        </div>

        {/* 등록 폼 */}
        <div style={{
          background:'#fff',borderRadius:14,padding:'18px 20px',
          boxShadow:'var(--shadow-sm)',marginBottom:18,
        }}>
          <div style={{fontSize:14,fontWeight:800,marginBottom:4}}>이메일 사전 등록</div>
          <div style={{fontSize:12,color:'#718096',marginBottom:14,lineHeight:1.6}}>
            등록된 이메일로 <b>처음 가입</b>하면 자동으로 무료 분석권이 지급돼요.
            이미 가입된 이메일이면 등록 즉시 지급됩니다. (이메일당 1회)
          </div>

          <div className="free-grant-form" style={{
            display:'grid',
            gridTemplateColumns:'minmax(200px, 2fr) 130px 130px 70px 70px 1fr auto',
            gap:10,alignItems:'end',
          }}>
            <label>
              <span style={{fontSize:11,fontWeight:700,color:'#4a5568'}}>이메일</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="parent@example.com"
                style={{
                  width:'100%',marginTop:4,padding:'10px 12px',
                  border:'1px solid #e2e8f0',borderRadius:10,fontSize:13,
                  fontFamily:'inherit',
                }}
              />
            </label>
            <label>
              <span style={{fontSize:11,fontWeight:700,color:'#4a5568'}}>아이 이름</span>
              <input
                type="text"
                value={childName}
                onChange={(e) => setChildName(e.target.value)}
                placeholder="예: 김민준"
                style={{
                  width:'100%',marginTop:4,padding:'10px 12px',
                  border:'1px solid #e2e8f0',borderRadius:10,fontSize:13,
                  fontFamily:'inherit',
                }}
              />
            </label>
            <label>
              <span style={{fontSize:11,fontWeight:700,color:'#4a5568'}}>부모 이름</span>
              <input
                type="text"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                placeholder="예: 김영희"
                style={{
                  width:'100%',marginTop:4,padding:'10px 12px',
                  border:'1px solid #e2e8f0',borderRadius:10,fontSize:13,
                  fontFamily:'inherit',
                }}
              />
            </label>
            <label>
              <span style={{fontSize:11,fontWeight:700,color:'#4a5568'}}>회수</span>
              <input
                type="number" min={1} max={50}
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
                style={{
                  width:'100%',marginTop:4,padding:'10px 12px',
                  border:'1px solid #e2e8f0',borderRadius:10,fontSize:13,
                  fontFamily:'inherit',textAlign:'right',
                }}
              />
            </label>
            <label>
              <span style={{fontSize:11,fontWeight:700,color:'#4a5568'}}>만료</span>
              <input
                type="number" min={1} max={24}
                value={expiresMonths}
                onChange={(e) => setExpiresMonths(e.target.value)}
                placeholder="개월"
                style={{
                  width:'100%',marginTop:4,padding:'10px 12px',
                  border:'1px solid #e2e8f0',borderRadius:10,fontSize:13,
                  fontFamily:'inherit',textAlign:'right',
                }}
              />
            </label>
            <label>
              <span style={{fontSize:11,fontWeight:700,color:'#4a5568'}}>메모 (선택)</span>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="예: 베타테스터, 이벤트 당첨"
                style={{
                  width:'100%',marginTop:4,padding:'10px 12px',
                  border:'1px solid #e2e8f0',borderRadius:10,fontSize:13,
                  fontFamily:'inherit',
                }}
              />
            </label>
            <button
              onClick={submit}
              disabled={submitting}
              style={{
                padding:'10px 18px',borderRadius:10,
                background:'#2c5f8d',border:'1px solid #2c5f8d',color:'#fff',
                fontSize:13,fontWeight:700,
                cursor: submitting ? 'wait' : 'pointer',
                opacity: submitting ? 0.7 : 1,
                fontFamily:'inherit',whiteSpace:'nowrap',
              }}>
              {submitting ? "등록 중..." : "+ 등록"}
            </button>
          </div>

          {feedback && (
            <div style={{
              marginTop:14,padding:'10px 12px',borderRadius:10,fontSize:12,
              background: feedback.tone === 'ok' ? '#27ae6018' : '#e74c3c18',
              color: feedback.tone === 'ok' ? '#1f7a3f' : '#c0392b',
            }}>
              {feedback.msg}
            </div>
          )}
        </div>

        {error && (
          <div style={{background:'#fef9f0',border:'1px solid #e74c3c44',borderRadius:12,padding:'12px 14px',marginBottom:16,fontSize:13,color:'#c0392b'}}>
            불러오지 못했어요: {error}
          </div>
        )}

        {/* 목록 */}
        <div className="admin-table-wrap">
          <div style={{
            display:'grid',
            gridTemplateColumns:'minmax(180px,2fr) 110px 110px 60px 60px 1fr 140px 100px 100px',
            gap:8,padding:'12px 18px',
            background:'#f7f8fa',borderBottom:'1px solid #eef0f4',
            fontSize:11,fontWeight:700,color:'#718096',textTransform:'uppercase',letterSpacing:.4,
          }}>
            <div>이메일</div>
            <div>아이 이름</div>
            <div>부모 이름</div>
            <div style={{textAlign:'center'}}>회수</div>
            <div style={{textAlign:'center'}}>만료</div>
            <div>메모</div>
            <div>등록일</div>
            <div>상태</div>
            <div></div>
          </div>
          {loading ? (
            <div style={{padding:'40px 0',textAlign:'center'}}>
              <div className="spinner" style={{display:'inline-block'}}/>
            </div>
          ) : rows.length === 0 ? (
            <div style={{padding:'40px 0',textAlign:'center',color:'#718096',fontSize:13}}>
              아직 등록된 무료권이 없어요.
            </div>
          ) : (
            rows.map((r) => {
              const granted = !!r.granted_at;
              const isEditing = editingId === r.id;
              return (
                <div key={r.id} style={{
                  display:'grid',
                  gridTemplateColumns:'minmax(180px,2fr) 110px 110px 60px 60px 1fr 140px 100px 100px',
                  gap:8,padding:'14px 18px',
                  borderBottom:'1px solid #f0f2f5',alignItems:'center',
                  fontSize:13,
                  background: granted ? '#fafbfc' : '#fff',
                }}>
                  <div>
                    <div style={{fontWeight:700}}>{r.email}</div>
                    {granted && r.granted_user?.display_name && (
                      <div style={{fontSize:11,color:'#718096'}}>
                        → {r.granted_user.display_name}
                      </div>
                    )}
                  </div>
                  <div>
                    {isEditing ? (
                      <input
                        type="text" value={editChild}
                        onChange={(e) => setEditChild(e.target.value)}
                        placeholder="아이 이름"
                        style={{width:'100%',padding:'6px 8px',border:'1px solid #e2e8f0',borderRadius:8,fontSize:12,fontFamily:'inherit'}}
                      />
                    ) : (
                      <span style={{color: r.child_name ? '#2d3748' : '#cbd5e0',fontSize:12}}>
                        {r.child_name || "-"}
                      </span>
                    )}
                  </div>
                  <div>
                    {isEditing ? (
                      <input
                        type="text" value={editParent}
                        onChange={(e) => setEditParent(e.target.value)}
                        placeholder="부모 이름"
                        style={{width:'100%',padding:'6px 8px',border:'1px solid #e2e8f0',borderRadius:8,fontSize:12,fontFamily:'inherit'}}
                      />
                    ) : (
                      <span style={{color: r.parent_name ? '#2d3748' : '#cbd5e0',fontSize:12}}>
                        {r.parent_name || "-"}
                      </span>
                    )}
                  </div>
                  <div style={{textAlign:'center',fontWeight:700,fontVariantNumeric:'tabular-nums'}}>
                    {r.credits}회
                  </div>
                  <div style={{textAlign:'center',color:'#4a5568'}}>
                    {r.expires_months}개월
                  </div>
                  <div style={{color:'#4a5568',fontSize:12,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={r.note ?? ''}>
                    {r.note || "-"}
                  </div>
                  <div style={{color:'#718096',fontVariantNumeric:'tabular-nums',fontSize:12}}>
                    {formatDate(r.created_at)}
                  </div>
                  <div>
                    {granted ? (
                      <span style={{padding:'4px 10px',borderRadius:9999,fontSize:11,fontWeight:700,background:'#27ae6022',color:'#1f7a3f'}}
                            title={`지급일 ${formatDate(r.granted_at)}`}>
                        지급 완료
                      </span>
                    ) : (
                      <span style={{padding:'4px 10px',borderRadius:9999,fontSize:11,fontWeight:700,background:'#ff993322',color:'#e6852b'}}>
                        대기 중
                      </span>
                    )}
                  </div>
                  <div style={{textAlign:'right',display:'flex',gap:4,justifyContent:'flex-end'}}>
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => saveEdit(r)}
                          style={{
                            padding:'6px 10px',borderRadius:8,fontSize:11,fontWeight:700,
                            background:'#2c5f8d',border:'1px solid #2c5f8d',color:'#fff',
                            cursor:'pointer',fontFamily:'inherit',
                          }}>
                          저장
                        </button>
                        <button
                          onClick={cancelEdit}
                          style={{
                            padding:'6px 10px',borderRadius:8,fontSize:11,fontWeight:700,
                            background:'#fff',border:'1px solid #e2e8f0',color:'#718096',
                            cursor:'pointer',fontFamily:'inherit',
                          }}>
                          취소
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEdit(r)}
                          title="이름 수정"
                          style={{
                            padding:'6px 10px',borderRadius:8,fontSize:11,fontWeight:700,
                            background:'#fff',border:'1px solid #e2e8f0',color:'#4a5568',
                            cursor:'pointer',fontFamily:'inherit',
                          }}>
                          수정
                        </button>
                        {!granted && (
                          <button
                            onClick={() => remove(r)}
                            style={{
                              padding:'6px 10px',borderRadius:8,fontSize:11,fontWeight:700,
                              background:'#fff',border:'1px solid #e2e8f0',color:'#718096',
                              cursor:'pointer',fontFamily:'inherit',
                            }}>
                            삭제
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
}
